import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import type { ChatScopeFilters } from "../../api";
import type { ContextualAsk } from "../../chat/AskContext";
import type { FilterOption } from "../FilterMultiSelect";
import { defaultAskSuggestions } from "../../chat/askScope";

export function useAskScopeUpdate(
  target: ContextualAsk,
  userOptions: FilterOption[],
  onChange: (target: ContextualAsk) => void,
) {
  const { t } = useTranslation();
  const isSession = Boolean(target.filters.recordingIds);
  return (
    next: ChatScopeFilters,
    clearSelection = false,
    datePreset = target.datePreset,
    runDates = target.runSelection,
  ) => {
    const runSelection = clearSelection ? undefined : runDates;
    let baseInstanceIds = clearSelection ? undefined : target.baseInstanceIds;
    if (next.instanceIds && runSelection) {
      const explicitRuns = target.contextItems?.filter(item => item.kind === "instance");
      baseInstanceIds = runSelection.runs
        .filter(
          run =>
            (!runSelection.selectedIds || runSelection.selectedIds.includes(run.id)) &&
            (!next.userIds || next.userIds.includes(run.userId)) &&
            (!runSelection.from || run.to >= runSelection.from) &&
            (!runSelection.to || run.from <= runSelection.to),
        )
        .map(run => run.id);
      next = {
        ...next,
        from: undefined,
        to: undefined,
        instanceIds: explicitRuns?.length
          ? baseInstanceIds.filter(id => explicitRuns.some(item => item.id === id))
          : baseInstanceIds,
      };
    }
    const people = next.userIds ?? userOptions.map(option => option.id);
    const allPeople =
      userOptions.length > 0 &&
      people.length === userOptions.length &&
      userOptions.every(option => people.includes(option.id));
    const peopleLabel = allPeople
      ? t("watchlist.all_people")
      : people.length === 1
        ? (userOptions.find(option => option.id === people[0])?.label ?? people[0])
        : t("dashboard.people_selected", { count: people.length });
    const dateLabel =
      !next.from || new Date(next.from).getTime() === 0
        ? t("dashboard.all_time")
        : `${format(new Date(next.from), "dd MMM yyyy")} – ${format(new Date(new Date(next.to ?? Date.now()).getTime() - 1), "dd MMM yyyy")}`;
    const dates = runSelection
      ? runSelection.from || runSelection.to
        ? `${runSelection.from} – ${runSelection.to}`
        : t("dashboard.all_time")
      : dateLabel;
    const runCount = next.instanceIds
      ? t("chat_selected_runs", { count: next.instanceIds.length })
      : undefined;
    const details = [peopleLabel, dates, runCount].filter(Boolean).join(", ");
    const sessionDetails = [target.sessionDetails, next.processes?.join(", ")]
      .filter(Boolean)
      .join(", ");
    onChange({
      ...target,
      filters: next,
      runSelection,
      baseInstanceIds,
      datePreset,
      customScope: undefined,
      context: target.page?.kind === "overview" ? undefined : target.context,
      page: target.page ? { ...target.page, insight: undefined } : undefined,
      ...(isSession && !next.recordingIds
        ? {
            sessionLabel: undefined,
            sessionDetails: undefined,
            page: { kind: "team" as const },
            sessionProcesses: undefined,
            suggestions: defaultAskSuggestions(t),
          }
        : {}),
      ...(clearSelection ? { contextItems: undefined } : {}),
      empty:
        next.userIds?.length === 0 ||
        next.processes?.length === 0 ||
        next.instanceIds?.length === 0,
      label:
        isSession && next.recordingIds
          ? `${target.sessionLabel ?? target.label}${sessionDetails ? ` (${sessionDetails})` : ""}`
          : `${next.processes?.join(", ") ?? t("watchlist.all_processes")} (${details})`,
    });
  };
}
